// Layer 3 (Transformation): downstream constraint check for recommended actions.

import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { Reading } from '@/versions/v1/schema/reading';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { SERIES_BY_ID } from '@/versions/v1/shared/bunker-hill-series';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';

const READING_BY_ID = new Map(READINGS.map((row) => [row.id, row]));

export type ConstraintCheck = {
  reading: Reading;
  currentValue: number;
  limitValue: number;
  headroom: number;
  unit: string;
};

export type ConstraintChip = {
  label: string;
  title: string;
};

export type ConstraintLine = {
  text: string;
};

function readingForId(readingId: string): Reading | undefined {
  return READING_BY_ID.get(readingId);
}

function currentValueForReading(reading: Reading, state: SessionState): number | undefined {
  const mapping = TAG_MAPPING.find((row) => row.readingId === reading.id);
  const seriesId = mapping?.seriesId;
  if (!seriesId || !SERIES_BY_ID[seriesId]) {
    const fallback = READINGS.find((row) => row.id === reading.id);
    return fallback?.value;
  }
  const view = seriesAt(seriesId, state);
  const latest = view.points[view.points.length - 1];
  return latest?.value ?? undefined;
}

function limitForReading(reading: Reading): number | undefined {
  if (reading.spec?.hi !== undefined) return reading.spec.hi;
  if (reading.spec?.lo !== undefined) return reading.spec.lo;
  return reading.design;
}

function headroomFor(
  current: number,
  limit: number,
  reading: Reading,
): number {
  if (reading.spec?.hi !== undefined) {
    return reading.higherIsBetter === false ? limit - current : current - limit;
  }
  if (reading.spec?.lo !== undefined) {
    return reading.higherIsBetter === false ? current - limit : limit - current;
  }
  return 0;
}

function formatReadingValue(value: number, reading: Reading): string {
  const decimals = reading.decimals ?? 1;
  const unit = reading.unit ?? '';
  return unit ? `${value.toFixed(decimals)} ${unit}` : value.toFixed(decimals);
}

function shortLabelForReading(reading: Reading): string {
  return reading.shortLabel ?? reading.label;
}

function limitComparator(reading: Reading): string {
  if (reading.spec?.hi !== undefined) return '<';
  if (reading.spec?.lo !== undefined) return '>';
  return reading.higherIsBetter === false ? '<' : '>';
}

export function constraintCheckForAction(
  action: RecommendedAction,
  _caseItem: Case,
  state: SessionState,
): ConstraintCheck | null {
  const link = 'constraint' in action ? action.constraint : undefined;
  if (!link?.readingId) return null;
  const reading = readingForId(link.readingId);
  if (!reading) return null;
  const current = currentValueForReading(reading, state);
  const limit = limitForReading(reading);
  if (current === undefined || limit === undefined) return null;
  const headroom = headroomFor(current, limit, reading);
  return {
    reading,
    currentValue: current,
    limitValue: limit,
    headroom,
    unit: reading.unit ?? '',
  };
}

export function constraintChipForAction(
  action: RecommendedAction,
  caseItem: Case,
  state: SessionState,
): ConstraintChip | null {
  const check = constraintCheckForAction(action, caseItem, state);
  if (!check) return null;
  const shortLabel = shortLabelForReading(check.reading);
  const cmp = limitComparator(check.reading);
  const limitStr = formatReadingValue(check.limitValue, check.reading);
  const label = `checked · ${shortLabel} ${cmp} ${limitStr}`;
  const currentStr = formatReadingValue(check.currentValue, check.reading);
  const title = `Checked against ${check.reading.label} — ${currentStr} now, limit ${limitStr}, headroom ${check.headroom.toFixed(1)} pt`;
  return { label, title };
}

export function constraintLineForAction(
  action: RecommendedAction,
  caseItem: Case,
  state: SessionState,
): ConstraintLine | null {
  const check = constraintCheckForAction(action, caseItem, state);
  if (!check) return null;
  const currentStr = formatReadingValue(check.currentValue, check.reading);
  const limitStr = formatReadingValue(check.limitValue, check.reading);
  return {
    text: `Checked against ${check.reading.label} — ${currentStr} now, limit ${limitStr}, headroom ${check.headroom.toFixed(1)} pt`,
  };
}
